import { User, Session, LoginCredentials, CreateUserData, UserRole, Permission } from '../types/auth';

class AuthService {
  private readonly STORAGE_KEY = 'prime_scada_session';
  private users: User[] = [];
  private sessions: Session[] = [];

  constructor() {
    this.initializeDefaultUsers();
    this.loadFromStorage();
  }

  // Inicializar usuários padrão
  private initializeDefaultUsers(): void {
    this.users = [
      {
        id: '1',
        username: 'admin',
        email: 'admin@primewind.com',
        role: UserRole.ADMIN,
        fullName: 'Administrador do Sistema',
        isActive: true,
        createdAt: new Date(),
        permissions: Object.values(Permission)
      },
      {
        id: '2',
        username: 'operador',
        email: 'operador@primewind.com',
        role: UserRole.OPERATOR,
        fullName: 'Operador Principal',
        isActive: true,
        createdAt: new Date(),
        permissions: [
          Permission.TURBINE_VIEW,
          Permission.TURBINE_CONTROL,
          Permission.DATA_VIEW,
          Permission.ALARM_VIEW,
          Permission.ALARM_ACK,
          Permission.REPORT_VIEW
        ]
      },
      {
        id: '3',
        username: 'manutencao',
        email: 'manutencao@primewind.com',
        role: UserRole.MAINTENANCE,
        fullName: 'Técnico de Manutenção',
        isActive: true,
        createdAt: new Date(),
        permissions: [
          Permission.TURBINE_VIEW,
          Permission.TURBINE_MAINTENANCE,
          Permission.DATA_VIEW,
          Permission.ALARM_VIEW,
          Permission.REPORT_VIEW
        ]
      },
      {
        id: '4',
        username: 'supervisor',
        email: 'supervisor@primewind.com',
        role: UserRole.VIEWER,
        fullName: 'Supervisor de Operações',
        isActive: true,
        createdAt: new Date(),
        permissions: [
          Permission.TURBINE_VIEW,
          Permission.DATA_VIEW,
          Permission.DATA_EXPORT,
          Permission.ALARM_VIEW,
          Permission.REPORT_VIEW,
          Permission.REPORT_EXPORT
        ]
      }
    ];
  }

  // Login
  async login(credentials: LoginCredentials): Promise<{ success: boolean; user?: User; session?: Session; error?: string }> {
    const user = this.users.find(u => 
      u.username === credentials.username && 
      u.isActive
    );

    if (!user) {
      return { success: false, error: 'Usuário não encontrado ou inativo' };
    }

    // Validação de senha (simplificada para demo)
    const isValidPassword = this.validatePassword(credentials.username, credentials.password);
    if (!isValidPassword) {
      return { success: false, error: 'Senha incorreta' };
    }

    // Criar sessão
    const session = this.createSession(user);
    
    // Atualizar último login
    user.lastLogin = new Date();

    // Salvar no storage
    this.saveToStorage();

    return { success: true, user, session };
  }

  // Validação de senha (simplificada)
  private validatePassword(username: string, password: string): boolean {
    const passwordMap: { [key: string]: string } = {
      'admin': 'admin123',
      'operador': 'oper123',
      'manutencao': 'manut123',
      'supervisor': 'super123'
    };

    return passwordMap[username] === password;
  }

  // Criar sessão
  private createSession(user: User): Session {
    const session: Session = {
      id: this.generateId(),
      userId: user.id,
      token: this.generateToken(),
      expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000), // 8 horas
      createdAt: new Date(),
      ipAddress: '127.0.0.1', // Simulado
      userAgent: navigator.userAgent,
      isActive: true
    };

    // Remover sessões antigas do usuário
    this.sessions = this.sessions.filter(s => s.userId !== user.id);
    this.sessions.push(session);

    return session;
  }

  // Logout
  logout(sessionId: string): void {
    this.sessions = this.sessions.filter(s => s.id !== sessionId);
    this.saveToStorage();
  }

  // Validar sessão
  validateSession(token: string): { valid: boolean; user?: User; session?: Session } {
    const session = this.sessions.find(s => 
      s.token === token && 
      s.isActive && 
      s.expiresAt > new Date()
    );

    if (!session) {
      return { valid: false };
    }

    const user = this.users.find(u => u.id === session.userId && u.isActive);
    if (!user) {
      return { valid: false };
    }

    return { valid: true, user, session };
  }

  // Renovar sessão
  refreshSession(token: string): { success: boolean; session?: Session } {
    const validation = this.validateSession(token);
    if (!validation.valid || !validation.user) {
      return { success: false };
    }

    const newSession = this.createSession(validation.user);
    this.saveToStorage();

    return { success: true, session: newSession };
  }

  // Gerenciamento de usuários
  getAllUsers(): User[] {
    return this.users.filter(u => u.isActive);
  }

  getUserById(id: string): User | undefined {
    return this.users.find(u => u.id === id && u.isActive);
  }

  createUser(userData: CreateUserData): { success: boolean; user?: User; error?: string } {
    // Verificar se username já existe
    if (this.users.some(u => u.username === userData.username)) {
      return { success: false, error: 'Nome de usuário já existe' };
    }

    // Verificar se email já existe
    if (this.users.some(u => u.email === userData.email)) {
      return { success: false, error: 'Email já existe' };
    }

    const newUser: User = {
      id: this.generateId(),
      username: userData.username,
      email: userData.email,
      role: userData.role,
      fullName: userData.fullName,
      isActive: true,
      createdAt: new Date(),
      permissions: userData.permissions
    };

    this.users.push(newUser);
    this.saveToStorage();

    return { success: true, user: newUser };
  }

  updateUser(id: string, updates: Partial<User>): { success: boolean; user?: User; error?: string } {
    const userIndex = this.users.findIndex(u => u.id === id);
    if (userIndex === -1) {
      return { success: false, error: 'Usuário não encontrado' };
    }

    // Não permitir alterar username do admin
    if (this.users[userIndex].username === 'admin' && updates.username) {
      return { success: false, error: 'Não é possível alterar o username do administrador' };
    }

    this.users[userIndex] = { ...this.users[userIndex], ...updates };
    this.saveToStorage();

    return { success: true, user: this.users[userIndex] };
  }

  deactivateUser(id: string): { success: boolean; error?: string } {
    const user = this.users.find(u => u.id === id);
    if (!user) {
      return { success: false, error: 'Usuário não encontrado' };
    }

    // Não permitir desativar admin
    if (user.username === 'admin') {
      return { success: false, error: 'Não é possível desativar o administrador' };
    }

    user.isActive = false;
    
    // Remover sessões ativas do usuário
    this.sessions = this.sessions.filter(s => s.userId !== id);
    
    this.saveToStorage();
    return { success: true };
  }

  // Sessões ativas
  getActiveSessions(): Session[] {
    return this.sessions.filter(s => 
      s.isActive && 
      s.expiresAt > new Date()
    );
  }

  terminateSession(sessionId: string): boolean {
    const sessionIndex = this.sessions.findIndex(s => s.id === sessionId);
    if (sessionIndex === -1) return false;

    this.sessions.splice(sessionIndex, 1);
    this.saveToStorage();
    return true;
  }

  // Utilitários
  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private generateToken(): string {
    return btoa(Math.random().toString(36).substr(2, 15) + Date.now().toString(36));
  }

  private saveToStorage(): void {
    try {
      const data = {
        users: this.users,
        sessions: this.sessions.map(s => ({
          ...s,
          expiresAt: s.expiresAt.toISOString(),
          createdAt: s.createdAt.toISOString()
        }))
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Erro ao salvar no localStorage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        if (parsed.users && Array.isArray(parsed.users)) {
          // Manter usuários padrão e adicionar outros
          const storedUsers = parsed.users.filter((u: User) => 
            !this.users.some(defaultUser => defaultUser.username === u.username)
          );
          this.users.push(...storedUsers);
        }
        if (parsed.sessions && Array.isArray(parsed.sessions)) {
          this.sessions = parsed.sessions.map((s: any) => ({
            ...s,
            expiresAt: new Date(s.expiresAt),
            createdAt: new Date(s.createdAt)
          })).filter((s: Session) => s.expiresAt > new Date());
        }
      }
    } catch (error) {
      console.error('Erro ao carregar do localStorage:', error);
    }
  }

  // Verificação de permissões
  hasPermission(user: User, permission: Permission): boolean {
    return user.permissions.includes(permission);
  }

  hasRole(user: User, role: UserRole): boolean {
    return user.role === role;
  }

  // Permissões por role
  getDefaultPermissions(role: UserRole): Permission[] {
    switch (role) {
      case UserRole.ADMIN:
        return Object.values(Permission);
      
      case UserRole.OPERATOR:
        return [
          Permission.TURBINE_VIEW,
          Permission.TURBINE_CONTROL,
          Permission.DATA_VIEW,
          Permission.ALARM_VIEW,
          Permission.ALARM_ACK,
          Permission.REPORT_VIEW
        ];
      
      case UserRole.MAINTENANCE:
        return [
          Permission.TURBINE_VIEW,
          Permission.TURBINE_MAINTENANCE,
          Permission.DATA_VIEW,
          Permission.ALARM_VIEW,
          Permission.REPORT_VIEW
        ];
      
      case UserRole.VIEWER:
        return [
          Permission.TURBINE_VIEW,
          Permission.DATA_VIEW,
          Permission.ALARM_VIEW,
          Permission.REPORT_VIEW
        ];
      
      default:
        return [Permission.TURBINE_VIEW, Permission.DATA_VIEW];
    }
  }
}

export default new AuthService();